import { MenuItem, Stack, Typography } from '@mui/material';
import { Popover } from '@mui/material';
import { IconButton, colors } from '@sweep-io/sweep-design';
import { Delete, MoreActionsHorizontal, NotePencil } from '@sweep-io/sweep-design/dist/icons';
import usePopover from '../../../../hooks/usePopover';
import useConfirm from '../../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import ConfirmDestructiveDialog from '../../../common/dialogs/ConfirmDestructiveDialog';

interface PromptCardActionsProps {
  onEdit: (e: React.MouseEvent<HTMLElement>) => void;
  onDelete: () => void;
  isDisabled: boolean;
}

export const PromptCardActions = ({ onEdit, onDelete, isDisabled }: PromptCardActionsProps) => {
  const { anchorElement, anchorRef, openPopover, closePopover, isPopoverOpen } = usePopover();
  const { openConfirm, onCancel, onConfirm: onConfirmDelete } = useConfirm();

  const onDeleteClick = async () => {
    const isConfirmed = await openConfirm(
      <ConfirmDestructiveDialog
        open
        onConfirm={onConfirmDelete}
        onClose={onCancel}
        title="Are you sure?"
        confirmText="Delete"
        cancelText="Cancel"
      >
        Deleting this category will delete all the prompts it contains.
      </ConfirmDestructiveDialog>,
    );
    if (isConfirmed) {
      onDelete();
    }
  };

  return (
    <Stack
      onClick={(e) => e.stopPropagation()}
      className="prompt-card-actions"
      sx={{ display: isPopoverOpen ? 'flex' : 'none' }}
    >
      <IconButton
        variant="flat"
        size="tiny"
        disabled={isDisabled}
        pressed={isPopoverOpen}
        onClick={openPopover}
        ref={anchorRef}
      >
        <MoreActionsHorizontal size="small" color={colors.grey[800]} />
      </IconButton>

      <Popover
        anchorEl={anchorElement}
        open={isPopoverOpen}
        onClose={() => closePopover()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Stack p={1}>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onEdit(e);
              closePopover();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '150px',
            }}
          >
            <NotePencil size="medium" />
            <Typography variant="body">Edit</Typography>
          </MenuItem>

          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick();
              closePopover();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Delete color={colors.blush[600]} size="medium" />
            <Typography variant="body" color={colors.blush[600]}>
              Delete
            </Typography>
          </MenuItem>
        </Stack>
      </Popover>
    </Stack>
  );
};
